import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAppInfo } from 'hooks/useAppInfo'
import { useUserInfo } from 'hooks/useUserInfo'
import terms from 'services/terms-and-conditions/terms/data'
import termsUsBranch from 'services/terms-and-conditions/terms/data-us-branch'
import { toW8Term, useW8Document } from 'services/w8-document'
import { TermsList } from 'components/TermsList'
import { handleSelectTermsAndConditions, normalizeLanguage } from 'utils'
import { LanguagesEnum } from 'common/enums/languages'
import { Term } from 'services/terms-and-conditions/types'

import { interWbSession } from '@interco/inter-webview-bridge'

const trackW8DocumentError = (error: unknown) => {
  interWbSession.requestAnalytics('GLOBAL_ACCOUNT_WEBVIEW', {
    eventId: 'ERROR_GET_W8_DOCUMENT',
    error: JSON.stringify(error),
  })
}

export function TermsAndConditions() {
  const appInfo = useAppInfo()
  const userInfo = useUserInfo()

  const selectedLanguage = normalizeLanguage(appInfo?.language, LanguagesEnum.EN_US)
  const hasBranches = userInfo.hasBranches === true

  const {
    data: w8Document,
    isError: isW8Error,
    error: w8Error,
  } = useW8Document({
    enabled: hasBranches,
  })

  useEffect(() => {
    if (isW8Error) {
      trackW8DocumentError(w8Error)
    }
  }, [isW8Error, w8Error])

  const baseTerms = useMemo(
    () => (hasBranches ? termsUsBranch[selectedLanguage] : terms[selectedLanguage]),
    [hasBranches, selectedLanguage],
  )

  const termsData = useMemo(() => {
    if (!w8Document) {
      return baseTerms
    }

    return [...baseTerms, toW8Term(w8Document)]
  }, [baseTerms, w8Document])

  const userContext = useMemo(
    () => ({
      organization: appInfo?.organization ?? '',
      account: userInfo.account ?? '',
      userType: userInfo.userType ?? '',
      hasBranches: String(userInfo.hasBranches ?? ''),
      gaStatus: userInfo.gaStatus ?? '',
      lang: selectedLanguage,
    }),
    [
      appInfo?.organization,
      userInfo.account,
      userInfo.userType,
      userInfo.hasBranches,
      userInfo.gaStatus,
      selectedLanguage,
    ],
  )

  const isContextLoaded = Object.keys(appInfo ?? {}).length > 0 && Object.keys(userInfo).length > 0
  const hasTrackedView = useRef(false)

  useEffect(() => {
    if (!isContextLoaded || hasTrackedView.current) return

    hasTrackedView.current = true
    interWbSession.requestAnalytics('GLOBAL_ACCOUNT_WEBVIEW', {
      eventId: 'TERMS_AND_CONDITIONS_VIEW',
      ...userContext,
      termsCount: String(baseTerms.length),
    })
  }, [isContextLoaded, userContext, baseTerms.length])

  const handleSelectTerm = useCallback(
    (item: Term) => {
      interWbSession.requestAnalytics('GLOBAL_ACCOUNT_WEBVIEW', {
        eventId: 'TERMS_AND_CONDITIONS_SELECT',
        ...userContext,
        termId: item.id,
      })
      handleSelectTermsAndConditions(item)
    },
    [userContext],
  )

  return <TermsList termsData={termsData} onSelectTerm={handleSelectTerm} />
}
